import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Autocomplete from "react-google-autocomplete";
import { setPreload } from "../../server/CartsOpen";
import Input from "../containers/InputForm";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  DIGITS_ONLY_MESSAGE,
  DIGITS_ONLY_PATTERN,
  SIGNED_INTEGER_MESSAGE,
  SIGNED_INTEGER_PATTERN,
} from "../../ValidFunction";
import showAlert from "../../server/ShowAlert";
import {
  getExistingStreetCity,
  parseAddressFromPlace,
} from "../../utils/googleAddress";

const schema = yup
  .object({
    Name: yup
      .string()
      .transform((value) => (value == null ? "" : String(value).trim()))
      .matches(/^$|^[\u0590-\u05FFa-zA-Z\s]+$/, "שם חייב להכיל אותיות בלבד"),
    StreetNumber: yup
      .string()
      .required("חובה להכניס מס' בניין")
      .matches(DIGITS_ONLY_PATTERN, DIGITS_ONLY_MESSAGE),
    HouseNumber: yup
      .string()
      .required("חובה להכניס מס' דירה")
      .matches(DIGITS_ONLY_PATTERN, DIGITS_ONLY_MESSAGE),
    Floor: yup
      .string()
      .required("חובה להכניס קומה")
      .matches(SIGNED_INTEGER_PATTERN, SIGNED_INTEGER_MESSAGE),
    ZipCode: yup.string().matches(/^\d*$/, "מיקוד חייב להכיל ספרות בלבד"),
  })
  .required();

const NewAddress = ({
  pushAddress,
  setNewAddress,
  closeSignUpPopup,
  user,
  submitLabel = "שמור > ",
}) => {
  const dispatch = useDispatch();
  const existingStreetCity = getExistingStreetCity(user);
  const [streetCityInput, setStreetCityInput] = useState(
    existingStreetCity?.query ?? "",
  );
  const [locationValidated, setLocationValidated] = useState(
    Boolean(existingStreetCity),
  );
  const [selectedAddress, setSelectedAddress] = useState(
    existingStreetCity
      ? {
          streetName: existingStreetCity.streetName,
          city: existingStreetCity.city,
        }
      : { streetName: "", city: "" },
  );
  const [deliveryMeta, setDeliveryMeta] = useState({
    lat: null,
    lng: null,
    polygonId: null,
    stationId: null,
  });
  const [addressError, setAddressError] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      Name: user?.name || user?.addrOrderRecipientName || "",
      StreetNumber: user?.houseNumber || user?.addrHouse || "",
      HouseNumber: user?.apartment || user?.addrApartment || "",
      Floor: user?.addrFloor || user?.floor || "",
      ZipCode: user?.zipCode || user?.addrZipCode || "",
    },
  });

  const resetLocationSelection = (clearInput = false) => {
    setLocationValidated(false);
    setAddressError("");
    setSelectedAddress({ streetName: "", city: "" });
    setDeliveryMeta({
      lat: null,
      lng: null,
      polygonId: null,
      stationId: null,
    });
    if (clearInput) {
      setStreetCityInput("");
    }
  };

  const setLocation = (place) => {
    if (!place?.geometry) {
      showAlert({
        msgTitle: "אנא הזינו את שם הרחוב והעיר ובחרו מתוך האפשרויות המוצעות",
        msgTimer: "3000",
        msgType: "info",
      });
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const parsed = parseAddressFromPlace(place);
    if (!parsed.streetName || !parsed.city) {
      showAlert({
        msgTitle: "אנא הזינו את שם הרחוב ועיר המגורים",
        msgType: "error",
      });
      return;
    }

    const streetCityQuery = `${parsed.streetName}, ${parsed.city}`;
    setStreetCityInput(streetCityQuery);
    setSelectedAddress({
      streetName: parsed.streetName,
      city: parsed.city,
    });
    if (parsed.streetNumber) {
      setValue("StreetNumber", parsed.streetNumber, { shouldValidate: true });
    }
    if (parsed.zipCode) {
      setValue("ZipCode", parsed.zipCode);
    }

    setDeliveryMeta({
      lat,
      lng,
      polygonId: null,
      stationId: null,
    });
    setAddressError("");
    setLocationValidated(true);
  };

  const validateAddressSelection = () => {
    if (!locationValidated) {
      setAddressError("חובה להכניס רחוב ועיר");
      return false;
    }
    return true;
  };

  const saveAddress = async (data) => {
    if (!validateAddressSelection()) {
      return;
    }

    dispatch(setPreload(true));

    const addressToUpdate = {
      city: selectedAddress.city,
      streetName: selectedAddress.streetName,
      houseNumber: data.StreetNumber,
      apartment: data.HouseNumber,
      zipCode: data.ZipCode,
      floor: data.Floor,
      lat: deliveryMeta.lat,
      lng: deliveryMeta.lng,
      polygonId: deliveryMeta.polygonId,
      stationId: deliveryMeta.stationId,
    };
    const recipientName = String(data.Name ?? "").trim();
    if (recipientName) {
      addressToUpdate.name = recipientName;
    }

    try {
      await pushAddress(addressToUpdate);
      if (typeof closeSignUpPopup === "function") {
        closeSignUpPopup();
      } else if (typeof setNewAddress === "function") {
        setNewAddress(false);
      }
    } catch (e) {
      console.error("Error saving address:", e);
    } finally {
      dispatch(setPreload(false));
    }
  };

  return (
    <div className="add-address">
      <form
        className="register add-address__form"
        onSubmit={handleSubmit(saveAddress, validateAddressSelection)}
      >
        <span className="note">שדות חובה מסומנים ב*</span>

        <div className="add-address__row">
          <div className="add-address__cell add-address__cell--autocomplete">
            <span>
              <label className="p-title" htmlFor="google-address-search">
                רחוב ועיר*
              </label>
              <Autocomplete
                id="google-address-search"
                className="add-address__autocomplete-input hz-field--borderless"
                value={streetCityInput}
                options={{
                  componentRestrictions: { country: "ISR" },
                  types: ["geocode"],
                }}
                onPlaceSelected={setLocation}
                onChange={(e) => {
                  setStreetCityInput(e.target.value);
                  setAddressError("");
                  if (locationValidated) {
                    resetLocationSelection();
                  }
                }}
                placeholder="הזינו רחוב ועיר ובחרו מתוך האפשרויות המוצעות"
              />
              <div className="text-danger">{addressError || null}</div>
            </span>
          </div>
        </div>

        <div className="add-address__row">
          <div className="add-address__cell">
            <Input
              type="text"
              register={register}
              label="שם מקבל ההזמנה"
              placeholder="שם משפחה/שם משרד/שם חברה/שם מוסד"
              errors={errors}
              name="Name"
              className="hz-field--borderless"
            />
          </div>
        </div>

        <div className="add-address__row add-address__row--3">
          <div className="add-address__cell">
            <Input
              type="tel"
              register={register}
              label="מס' בניין*"
              errors={errors}
              name="StreetNumber"
              digitsOnly
              className="hz-field--borderless"
            />
          </div>
          <div className="add-address__cell">
            <Input
              type="text"
              register={register}
              label="קומה*"
              errors={errors}
              name="Floor"
              signedInteger
              className="hz-field--borderless"
            />
          </div>
          <div className="add-address__cell">
            <Input
              type="tel"
              register={register}
              label="מס' דירה*"
              errors={errors}
              name="HouseNumber"
              digitsOnly
              className="hz-field--borderless"
            />
          </div>
        </div>

        <div className="add-address__row">
          <div className="add-address__cell add-address__cell--zip">
            <Input
              type="tel"
              register={register}
              label="מיקוד"
              errors={errors}
              name="ZipCode"
              className="hz-field--borderless"
            />
          </div>
        </div>
        <span className="checkbox-and-btn">
          <button className="btn" type="submit">
            {submitLabel}
          </button>
        </span>
      </form>
    </div>
  );
};

export default NewAddress;
